function toId(row) {
  return String(row._id || row.id);
}

function fmtDate(d) {
  if (!d) return '—';
  try {
    const dt = new Date(`${d}T12:00:00`);
    const dd = String(dt.getDate()).padStart(2, '0');
    const mm = String(dt.getMonth() + 1).padStart(2, '0');
    const yyyy = dt.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  } catch {
    return '—';
  }
}

function buildCompanyNameMap(rows, companies) {
  const map = Object.fromEntries((companies || []).map(c => [toId(c), c.name]));
  for (const row of rows) {
    const cid = row.companyId?._id || row.companyId;
    if (cid && !map[String(cid)]) map[String(cid)] = '';
  }
  return map;
}

export function buildClientTransferPreview(username, data = {}) {
  const {
    companies = [],
    leadBank = [],
    spocs = [],
    meetings = [],
    calls = [],
    proposals = [],
  } = data;

  const companyRows = companies.filter(c => c.salesperson === username);
  const leadRows = leadBank.filter(l => l.assignedTo === username);
  const spocRows = spocs.filter(s => s.dailyAssignedTo === username);
  const meetingRows = meetings.filter(m => m.salesperson === username);
  const callRows = calls.filter(c => c.salesperson === username);
  const proposalRows = proposals.filter(p => p.salesperson === username);

  const coNameMap = buildCompanyNameMap(
    [...meetingRows, ...callRows, ...proposalRows, ...spocRows],
    companies
  );

  const items = {
    companies: companyRows.map(c => ({
      id: toId(c),
      name: c.name,
      city: c.city,
      label: `${c.name}${c.city ? ` · ${c.city}` : ''}`,
    })),
    leads: leadRows.map(l => ({
      id: toId(l),
      name: l.name,
      city: l.city,
      label: `${l.name}${l.city ? ` · ${l.city}` : ''}`,
    })),
    spocAssignments: spocRows.map(s => {
      const cid = s.companyId?._id || s.companyId;
      const coName = cid ? coNameMap[String(cid)] : '';
      return {
        id: toId(s),
        name: s.name,
        companyName: coName || '',
        label: `${s.name}${coName ? ` · ${coName}` : ''}`,
      };
    }),
    meetings: meetingRows.map(m => ({
      id: toId(m),
      date: m.date,
      companyName: coNameMap[String(m.companyId?._id || m.companyId)] || '',
      stage: m.stage || '',
      label: `${fmtDate(m.date)} · ${coNameMap[String(m.companyId?._id || m.companyId)] || 'Company'}${m.stage ? ` · ${m.stage}` : ''}`,
    })),
    calls: callRows.map(c => ({
      id: toId(c),
      date: c.date,
      companyName: coNameMap[String(c.companyId?._id || c.companyId)] || '',
      outcome: c.conversationOutcome || c.outcome || '',
      label: `${fmtDate(c.date)} · ${coNameMap[String(c.companyId?._id || c.companyId)] || 'Company'}${c.conversationOutcome || c.outcome ? ` · ${c.conversationOutcome || c.outcome}` : ''}`,
    })),
    proposals: proposalRows.map(p => ({
      id: toId(p),
      date: p.date,
      companyName: coNameMap[String(p.companyId?._id || p.companyId)] || '',
      label: `${fmtDate(p.date)} · ${p.sentToName || coNameMap[String(p.companyId?._id || p.companyId)] || 'Proposal'}`,
    })),
  };

  return {
    username,
    companies: items.companies.length,
    leads: items.leads.length,
    spocAssignments: items.spocAssignments.length,
    meetingsConducted: items.meetings.length,
    calls: items.calls.length,
    proposals: items.proposals.length,
    needsTransfer: items.companies.length + items.leads.length + items.spocAssignments.length > 0,
    items,
  };
}

export function mergeTransferPreview(apiPreview, clientPreview) {
  if (!apiPreview) return clientPreview;
  if (!clientPreview) return apiPreview;

  const keys = ['companies', 'leads', 'spocAssignments', 'meetings', 'calls', 'proposals'];
  const items = {};
  for (const key of keys) {
    const apiItems = apiPreview.items?.[key];
    const clientItems = clientPreview.items?.[key];
    items[key] = (Array.isArray(apiItems) && apiItems.length ? apiItems : clientItems) || [];
  }

  return {
    username: apiPreview.username || clientPreview.username,
    companies: items.companies.length,
    leads: items.leads.length,
    spocAssignments: items.spocAssignments.length,
    meetingsConducted: items.meetings.length,
    calls: items.calls.length,
    proposals: items.proposals.length,
    needsTransfer: items.companies.length + items.leads.length + items.spocAssignments.length > 0,
    items,
  };
}
